/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { Spacer, Icon, Button } from "@cd2/components"
import { useSkeem } from "../providers/SkeemProvider"
import { useTheme } from "@cd2/theme"
import { assert, isLight, lightenDarkenColor } from "@cd2/tools"
import { useAsyncCallback } from "../hooks/useAsyncCallback"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { useMutatorState } from "../hooks/useMutatorState"
import { Flex } from "./Flex"
import { PopoutInput } from "./PopoutInput"
import { IRecord } from "../types/schema"
import { useGlobalState } from "../utils/globalState"
import { icons } from "../utils/icons"
import { fuzzyMatch } from "../utils/string"
import { Fields } from "@cd2/forms"
import { useLocation } from "react-router-dom"
import React from "react"
import Tippy from "@tippyjs/react"
import { siteColors, siteTheme } from "../providers/ThemeProvider"

export async function switchCompany(companyId: string) {
  const $dialog = document.createElement("dialog")
  document.body.appendChild($dialog)
  $dialog.innerHTML = `<i class="fa fa-spinner fa-spin" style="font-size: 24px;"></i>`
  $dialog.style.background = "transparent"
  $dialog.style.border = "none"
  $dialog.showModal()

  const me = await window.skeem.fetchMe<IRecord<"skeem_users", "employee">>(["employee"])
  assert(me, "User not found")
  await window.skeem.mutate({
    skeem_users: {
      update: {
        id: me.id,
        company: { set: companyId },
        employee: me?.employee
          ? { update: { id: me.employee.id, company: { set: companyId } } }
          : undefined,
      },
    },
  })
  // Send message on broadcast channel to reload other tabs
  const channel = new BroadcastChannel("company-switch")
  channel.postMessage({ type: "company-switch" })
  const url = new URL(window.location.href)
  url.searchParams.set("did-switch-company", "true")
  window.location.href = url.toString()
}

export function listenForCompanySwitch() {
  console.log("Listening for company switch messages")
  const channel = new BroadcastChannel("company-switch")

  channel.onmessage = event => {
    console.log("Received company switch message:", event)

    // reload the page with an additional param of &did-switch-company=true
    const url = new URL(window.location.href)
    url.searchParams.set("did-switch-company", "true")
    window.location.href = url.toString()
  }
}

export function CompanySwitcher({ inAdminArea }: { inAdminArea?: boolean }) {
  const theme = useTheme()
  const skeem = useSkeem()
  const globalState = useGlobalState()
  const location = useLocation()

  type Company = IRecord<"companies", "name" | "isHeadCompany" | "isAdminCompany">
  const [state, mutate] = useMutatorState({
    currentCompany: null as Company | null,
    accessibleCompanies: [] as Company[],
    primaryCompany: null as null | Company,
    query: "",
  })

  useAsyncEffect(async () => {
    let accessibleCompanies = await skeem.fetch2<Company[]>({
      model: {
        name: "companies",
        attributes: ["name", "isHeadCompany", "isAdminCompany"],
        filter: {
          or: [
            { matches: { id: globalState.companyId } },
            {
              and: [
                !globalState.user.isAdmin && {
                  matches: { accessibleUsers: { some: { id: { session: true } } } },
                },
                { matches: { archived: false } },
              ].filter(Boolean),
            },
          ],
        },
        sort: { by: "name", direction: "asc" },
      },
    })

    const currentCompany = accessibleCompanies.find(company => company.id === globalState.companyId)
    assert(currentCompany, "User must have a company")

    const primaryCompany =
      accessibleCompanies.find(company => company.isAdminCompany) ??
      accessibleCompanies.find(company => company.isHeadCompany) ??
      null

    accessibleCompanies = accessibleCompanies.filter(el => !el.isAdminCompany)

    mutate.set({
      currentCompany,
      accessibleCompanies,
      primaryCompany,
      query: "",
    })
  }, [])

  const handleSwitch = useAsyncCallback(async (companyId: string) => {
    await switchCompany(companyId)
  })

  const hideOnRoutes = ["/rota"]
  if (hideOnRoutes.some(route => location.pathname.startsWith(route))) return null

  // not loaded
  if (!state.currentCompany) return null

  // not relevant
  if (state.accessibleCompanies.filter(el => el.id !== state.currentCompany!.id).length === 0)
    return null

  const background = lightenDarkenColor(theme.themeColor("primary"), 0.2)
  const textColor = isLight(background) ? "black" : "white"

  return (
    <Flex
      css={
        !inAdminArea &&
        css`
          background: ${lightenDarkenColor(theme.themeColor("primary"), 0.2)};
          padding: 8px 12px;
          color: ${textColor};
        `
      }
      alignItems="center"
    >
      {!state.currentCompany.isAdminCompany && (
        <React.Fragment>
          <span>
            Company: <b>{state.currentCompany.name}</b>
          </span>
          <Spacer size={32} horizontal />
        </React.Fragment>
      )}
      <div
        css={css`
          color: black;
        `}
      >
        <PopoutInput
          renderMain={() => (
            <div>
              <Icon icon={icons.subCompanies} />
              <Spacer horizontal />
              Switch
            </div>
          )}
          renderOptions={() => (
            <div>
              <Fields.Search
                ref={el => {
                  if (el?.matches(":focus")) return
                  el?.focus()
                  el?.select()
                }}
                value={state.query}
                onChange={mutate.setter("query")}
              />
              <div
                css={css`
                  max-height: 400px;
                  overflow: auto;
                `}
              >
                <PopoutInput.OptionsList
                  options={state.accessibleCompanies
                    .filter(el => fuzzyMatch([el.name], state.query))
                    .map(company => ({
                      label: [company.name, company.isHeadCompany && "(head)"]
                        .filter(Boolean)
                        .join(" "),
                      onClick: () => handleSwitch(company.id),
                      disabled: company.id === state.currentCompany!.id,
                    }))}
                />
              </div>
            </div>
          )}
        />
      </div>
      <Flex.Spacer />
      {state.primaryCompany && state.primaryCompany.id !== state.currentCompany.id && (
        <Button leftIcon={icons.back} onClick={() => handleSwitch(state.primaryCompany!.id)}>
          Back to {state.primaryCompany.isAdminCompany ? "Admin Area" : state.primaryCompany.name}
        </Button>
      )}
    </Flex>
  )
}

export function SidebarCompanySwitcher() {
  const skeem = useSkeem()
  const globalState = useGlobalState()
  const location = useLocation()

  type Company = IRecord<"companies", "name" | "isHeadCompany" | "isAdminCompany">
  const [state, mutate] = useMutatorState({
    currentCompany: null as Company | null,
    accessibleCompanies: [] as Company[],
    primaryCompany: null as null | Company,
    query: "",
  })

  useAsyncEffect(async () => {
    let accessibleCompanies = await skeem.fetch2<Company[]>({
      model: {
        name: "companies",
        attributes: ["name", "isHeadCompany", "isAdminCompany"],
        filter: {
          or: [
            { matches: { id: globalState.companyId } },
            {
              and: [
                !globalState.user.isAdmin && {
                  matches: { accessibleUsers: { some: { id: { session: true } } } },
                },
                { matches: { archived: false } },
              ].filter(Boolean),
            },
          ],
        },
        sort: { by: "name", direction: "asc" },
      },
    })

    const currentCompany = accessibleCompanies.find(company => company.id === globalState.companyId)
    assert(currentCompany, "User must have a company")

    const primaryCompany =
      accessibleCompanies.find(company => company.isAdminCompany) ??
      accessibleCompanies.find(company => company.isHeadCompany) ??
      null

    accessibleCompanies = accessibleCompanies.filter(el => !el.isAdminCompany)

    mutate.set({
      currentCompany,
      accessibleCompanies,
      primaryCompany,
      query: "",
    })
  }, [])

  const handleSwitch = useAsyncCallback(async (companyId: string) => {
    await switchCompany(companyId)
  })

  const hideOnRoutes = ["/rota"]
  if (hideOnRoutes.some(route => location.pathname.startsWith(route))) return null

  // not loaded
  if (!state.currentCompany) return null

  // not relevant
  if (state.accessibleCompanies.filter(el => el.id !== state.currentCompany!.id).length === 0)
    return null

  return (
    <div>
      <Tippy
        placement="right-start"
        trigger="click"
        appendTo={document.body}
        onShow={instance => {
          setTimeout(() => {
            if (instance.props.content && instance.props.content instanceof HTMLElement) {
              instance.props.content.querySelector("input")?.focus()
            }
          }, 100)
        }}
        content={
          <div
            css={css`
              background: white;
              border: ${siteColors.lightBorder} 1px solid;
              border-radius: ${siteTheme.borderRadius}px;
              padding: 8px;
            `}
          >
            {state.primaryCompany && state.primaryCompany.id !== state.currentCompany.id && (
              <React.Fragment>
                <Button
                  leftIcon={icons.back}
                  onClick={() => handleSwitch(state.primaryCompany!.id)}
                  css={css`
                    width: 100%;
                  `}
                  extendTheme={{ padding: { topAndBottom: 6, sides: 12 } }}
                >
                  <div
                    css={css`
                      font-size: 0.8em;
                    `}
                  >
                    Return to
                  </div>
                  <Spacer size={2} />
                  <b>
                    {state.primaryCompany.isAdminCompany ? "Admin Area" : state.primaryCompany.name}
                  </b>
                </Button>
                <Spacer size={8} />
              </React.Fragment>
            )}
            <Fields.Search
              ref={el => {
                if (el?.matches(":focus")) return
                el?.focus()
                el?.select()
              }}
              value={state.query}
              onChange={mutate.setter("query")}
              autoFocus
            />
            <div
              css={css`
                max-height: 400px;
                overflow: auto;
              `}
            >
              <PopoutInput.OptionsList
                options={state.accessibleCompanies
                  .filter(el => fuzzyMatch([el.name], state.query))
                  .map(company => ({
                    label: [company.name, company.isHeadCompany && "(head)"]
                      .filter(Boolean)
                      .join(" "),
                    onClick: () => handleSwitch(company.id),
                    disabled: company.id === state.currentCompany!.id,
                  }))}
              />
            </div>
          </div>
        }
        interactive
      >
        <div
          css={css`
            background: #fafafa;
            border: 1px solid rgba(0, 0, 0, 0.1);
            border-radius: 8px;
            cursor: pointer;
          `}
        >
          <div
            css={css`
              display: grid;
              grid-template-columns: 32px auto 32px;
              grid-gap: 8px;
              align-items: center;
              padding: 12px 8px;
              text-decoration: none;
              transition: 0.2s;
              color: #737585;
              border-radius: 8px;
              font-size: 14px;
            `}
          >
            <div
              css={css`
                justify-self: center;
              `}
            >
              <Icon icon={icons.subCompanies} />
            </div>
            Switch
            <Icon icon={icons.chevronRight} />
          </div>
        </div>
      </Tippy>
    </div>
  )
}
